(function () {
  'use strict';

  const Core = window.CityOpticsDryEyeFinderCore;
  if (!Core) return;

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  class DryEyeProductFinder extends HTMLElement {
    constructor() {
      super();
      this.answers = {};
      this.step = 0;
      this.completed = false;
      this.productsPromise = null;
      this.products = [];
      this.openedBy = null;
      this.sessionGeneration = 0;
      this.carouselObserver = null;
      this.carouselFrame = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundHashChange = this.openFromHash.bind(this);
    }

    connectedCallback() {
      if (this.dataset.ready === 'true') {
        window.addEventListener('hashchange', this.boundHashChange);
        requestAnimationFrame(() => this.openFromHash());
        return;
      }
      this.dataset.ready = 'true';

      this.dialog = this.querySelector('[data-dry-eye-finder-dialog]');
      this.openButton = this.querySelector('[data-dry-eye-finder-open]');
      this.screen = this.querySelector('[data-dry-eye-finder-screen]');
      this.progressWrap = this.querySelector('[data-dry-eye-finder-progress-wrap]');
      this.progress = this.querySelector('[data-dry-eye-finder-progress]');
      this.progressText = this.querySelector('[data-dry-eye-finder-progress-text]');
      this.backButton = this.querySelector('[data-dry-eye-finder-back]');
      this.continueButton = this.querySelector('[data-dry-eye-finder-continue]');
      this.closeButton = this.querySelector('[data-dry-eye-finder-close]');

      if (!this.dialog || !this.openButton || !this.screen) return;

      this.openButton.addEventListener('click', () => this.open(this.openButton));
      this.closeButton?.addEventListener('click', () => this.requestClose());
      this.backButton?.addEventListener('click', () => this.goBack());
      this.continueButton?.addEventListener('click', () => this.goForward());
      this.screen.addEventListener('change', (event) => this.onAnswerChange(event));
      this.screen.addEventListener('click', (event) => this.onScreenClick(event));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.requestClose();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.requestClose();
      });
      this.dialog.addEventListener('close', () => this.afterClose());
      this.dialog.addEventListener('keydown', this.boundKeydown);
      window.addEventListener('hashchange', this.boundHashChange);

      this.preloadProducts().catch(() => {});
      requestAnimationFrame(() => this.openFromHash());
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this.boundHashChange);
    }

    get collectionUrl() {
      return this.dataset.collectionUrl || '/collections/dry-eye';
    }

    get sectionId() {
      return this.dataset.sectionId || '';
    }

    open(trigger = null) {
      if (this.dialog.hasAttribute('open')) return;
      this.sessionGeneration += 1;
      this.openedBy = trigger instanceof HTMLElement
        ? trigger
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : this.openButton;
      this.completed = false;
      this.answers = {};
      this.step = 0;
      this.renderQuestion();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('dry-eye-finder-open');
      document.body.classList.add('dry-eye-finder-open');
      this.track('opened');
      requestAnimationFrame(() => this.querySelector('.dry-eye-finder__heading-wrap h2')?.focus());
      this.preloadProducts().catch(() => {});
    }

    openFromHash() {
      if (window.location.hash !== '#dry-eye-finder' || !this.dialog || this.dialog.hasAttribute('open')) return;
      this.open(this.openButton);
    }

    requestClose() {
      const answersWouldBeLost = !this.completed && Object.keys(this.answers).some((key) => this.answerHasValue(this.answers[key]));
      if (answersWouldBeLost) this.track('abandoned', { last_completed_step: this.lastCompletedStep() });
      this.sessionGeneration += 1;
      this.answers = {};
      this.close();
    }

    close() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.afterClose();
      }
    }

    afterClose() {
      document.documentElement.classList.remove('dry-eye-finder-open');
      document.body.classList.remove('dry-eye-finder-open');
      if (window.location.hash === '#dry-eye-finder') {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }
      this.openedBy?.focus();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      this.requestClose();
    }

    answerHasValue(value) {
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    }

    lastCompletedStep() {
      let completed = 0;
      for (let index = 0; index < Core.QUESTIONS.length; index += 1) {
        if (!Core.hasValidAnswer(Core.QUESTIONS[index], this.answers)) break;
        completed = index + 1;
      }
      return completed;
    }

    renderQuestion() {
      this.teardownCarousel();
      const question = Core.QUESTIONS[this.step];
      const selected = Array.isArray(this.answers[question.id])
        ? this.answers[question.id]
        : this.answers[question.id]
          ? [this.answers[question.id]]
          : [];
      const inputType = question.type === 'multiple' ? 'checkbox' : 'radio';
      const helpId = `DryEyeFinderQuestionHelp-${this.sectionId}`;
      const limitId = `DryEyeFinderSelectionLimit-${this.sectionId}`;
      const options = question.options
        .map((option) => {
          const id = `DryEyeFinder-${this.sectionId}-${question.id}-${option.id}`;
          const checked = selected.includes(option.id);
          return `
            <div class="dry-eye-finder__answer${checked ? ' is-selected' : ''}">
              <input
                class="dry-eye-finder__answer-input"
                type="${inputType}"
                id="${escapeHtml(id)}"
                name="${escapeHtml(question.id)}"
                value="${escapeHtml(option.id)}"
                aria-describedby="${escapeHtml(question.maxSelections ? `${helpId} ${limitId}` : helpId)}"
                ${checked ? 'checked' : ''}
              >
              <label class="dry-eye-finder__answer-label" for="${escapeHtml(id)}">
                <span>
                  ${escapeHtml(option.label)}
                  ${option.detail ? `<small class="dry-eye-finder__answer-detail">${escapeHtml(option.detail)}</small>` : ''}
                </span>
                <span class="dry-eye-finder__answer-mark" aria-hidden="true"></span>
              </label>
            </div>`;
        })
        .join('');

      this.screen.innerHTML = `
        <section class="dry-eye-finder__question" aria-labelledby="DryEyeFinderQuestion-${this.sectionId}">
          <fieldset>
            <legend id="DryEyeFinderQuestion-${this.sectionId}" tabindex="-1">${escapeHtml(question.title)}</legend>
            <p class="dry-eye-finder__question-help" id="${escapeHtml(helpId)}">${escapeHtml(question.help)}</p>
            ${
              question.maxSelections
                ? `<p class="dry-eye-finder__selection-limit" id="${escapeHtml(limitId)}" data-dry-eye-finder-selection-limit aria-live="polite" hidden></p>`
                : ''
            }
            <div class="dry-eye-finder__answers dry-eye-finder__answers--${question.type}">${options}</div>
          </fieldset>
        </section>`;

      this.progressWrap.hidden = false;
      this.progress.value = this.step + 1;
      this.progress.textContent = `${this.step + 1} of ${Core.QUESTIONS.length}`;
      this.progressText.textContent = `Question ${this.step + 1} of ${Core.QUESTIONS.length}`;
      this.backButton.hidden = this.step === 0;
      this.backButton.disabled = this.step === 0;
      this.continueButton.hidden = false;
      this.continueButton.disabled = !Core.hasValidAnswer(question, this.answers);
      this.continueButton.textContent = this.step === Core.QUESTIONS.length - 1 ? 'See my results' : 'Continue';
      this.completed = false;
    }

    onAnswerChange(event) {
      const input = event.target.closest('.dry-eye-finder__answer-input');
      if (!input) return;
      const question = Core.QUESTIONS[this.step];
      if (question.type === 'single') {
        this.answers[question.id] = input.value;
      } else {
        let selected = [...this.screen.querySelectorAll('.dry-eye-finder__answer-input:checked')].map((item) => item.value);
        const limitMessage = this.screen.querySelector('[data-dry-eye-finder-selection-limit]');
        let exceededLimit = false;
        if (question.maxSelections && selected.length > question.maxSelections) {
          exceededLimit = true;
          input.checked = false;
          selected = selected.filter((value) => value !== input.value);
          if (limitMessage) {
            limitMessage.textContent = 'You can select up to two symptoms.';
            limitMessage.hidden = false;
          }
        } else if (input.value === 'none' && input.checked) {
          selected = ['none'];
          this.screen.querySelectorAll('.dry-eye-finder__answer-input').forEach((item) => {
            item.checked = item.value === 'none';
          });
        } else if (input.checked) {
          selected = selected.filter((value) => value !== 'none');
          const noneInput = this.screen.querySelector('.dry-eye-finder__answer-input[value="none"]');
          if (noneInput) noneInput.checked = false;
        }
        if (limitMessage && !exceededLimit) {
          limitMessage.textContent = '';
          limitMessage.hidden = true;
        }
        this.answers[question.id] = selected;
      }

      this.screen.querySelectorAll('.dry-eye-finder__answer').forEach((answer) => {
        answer.classList.toggle('is-selected', Boolean(answer.querySelector('.dry-eye-finder__answer-input:checked')));
      });
      this.continueButton.disabled = !Core.hasValidAnswer(question, this.answers);
    }

    goBack() {
      if (this.step === 0) return;
      this.step -= 1;
      this.renderQuestion();
      this.focusHeading();
    }

    goForward() {
      const question = Core.QUESTIONS[this.step];
      if (!Core.hasValidAnswer(question, this.answers)) return;
      this.track('question_completed', {
        question_id: question.id,
        answer_count: Array.isArray(this.answers[question.id]) ? this.answers[question.id].length : 1,
        selection_mode: question.type === 'multiple' ? 'multiple' : 'single',
        answer_id_schema: question.id === 'symptoms' ? 'symptoms_v2' : `${question.id}_v1`,
      });
      if (this.step < Core.QUESTIONS.length - 1) {
        this.step += 1;
        this.renderQuestion();
        this.focusHeading();
        return;
      }
      this.showResult();
    }

    async showResult() {
      this.teardownCarousel();
      const sessionGeneration = this.sessionGeneration;
      const result = Core.calculateResult(this.answers);
      this.completed = true;
      this.progressWrap.hidden = true;
      this.backButton.hidden = true;
      this.continueButton.hidden = true;

      if (result.type === 'safety') {
        this.renderSafety(result);
        this.track('safety_stop');
        return;
      }
      if (result.type === 'itching') {
        this.renderItching();
        this.track('result_displayed', { result_type: 'itching_guidance' });
        return;
      }

      this.renderLoading();
      try {
        const products = await this.preloadProducts();
        if (sessionGeneration !== this.sessionGeneration || !this.dialog.hasAttribute('open')) return;
        const eligible = Core.filterProducts(products, result);
        if (eligible.length === 0) {
          this.renderNoMatch();
          this.track('result_displayed', { result_type: 'no_match', category: result.primary });
          return;
        }
        this.renderStandardResult(result, eligible);
        this.track('result_displayed', {
          result_type: 'standard',
          category: result.primary,
          primary_categories: result.primaryCategories.join('|'),
          primary_category_count: result.primaryCategories.length,
          secondary_category: result.secondary || undefined,
          product_count: eligible.length,
        });
      } catch (error) {
        if (sessionGeneration !== this.sessionGeneration || !this.dialog.hasAttribute('open')) return;
        console.warn('Dry Eye product finder could not load product data', error);
        this.renderUnavailable();
      }
    }

    renderLoading() {
      this.screen.innerHTML = `
        <section class="dry-eye-finder__state dry-eye-finder__state--loading" role="status" aria-live="polite">
          <span class="dry-eye-finder__loader" aria-hidden="true"></span>
          <h2 tabindex="-1">Finding suitable products…</h2>
          <p>We’re checking the complete Dry Eye collection against your answers.</p>
        </section>`;
      this.focusHeading();
    }

    renderSafety(result) {
      this.screen.innerHTML = `
        <section class="dry-eye-finder__state dry-eye-finder__state--safety">
          <p class="dry-eye-finder__eyebrow">Professional guidance recommended</p>
          <h2 tabindex="-1">These symptoms need professional assessment</h2>
          <p>This product finder cannot safely recommend an over-the-counter product based on your answers. Please seek prompt advice from an optometrist or medical professional. For severe or sudden symptoms, seek urgent medical care.</p>
          ${
            result.chemicalExposure
              ? '<p class="dry-eye-finder__urgent-note"><strong>If a chemical has entered your eye, rinse it immediately with clean running water and seek urgent professional advice.</strong></p>'
              : ''
          }
          <div class="dry-eye-finder__state-actions">
            <a class="button" href="${escapeHtml(this.dataset.bookUrl)}" data-dry-eye-finder-analytics="booking_clicked">Book an eye test</a>
            <a class="button button--secondary" href="${escapeHtml(this.dataset.storeUrl)}" data-dry-eye-finder-analytics="store_clicked">Find a store</a>
          </div>
        </section>`;
      this.focusHeading();
    }

    renderItching() {
      this.screen.innerHTML = `
        <section class="dry-eye-finder__state">
          <p class="dry-eye-finder__eyebrow">A different route may be more suitable</p>
          <h2 tabindex="-1">Your symptoms may need different guidance</h2>
          <p>Itching can have causes other than dry eye, so this tool cannot confidently recommend a dry-eye product from your answers.</p>
          <div class="dry-eye-finder__state-actions">
            <a class="button" href="${escapeHtml(this.dataset.bookUrl)}" data-dry-eye-finder-analytics="booking_clicked">Book an eye test</a>
            <a class="button button--secondary" href="${escapeHtml(this.collectionUrl)}">Browse all dry eye products</a>
            <button class="button button--tertiary" type="button" data-dry-eye-finder-restart>Start again</button>
          </div>
        </section>`;
      this.focusHeading();
    }

    renderNoMatch() {
      this.screen.innerHTML = `
        <section class="dry-eye-finder__state">
          <h2 tabindex="-1">We couldn’t find an exact match</h2>
          <p>We do not currently have a product that meets all of the requirements selected. Please ask our optometry team for help choosing a suitable option.</p>
          <div class="dry-eye-finder__state-actions">
            <a class="button" href="${escapeHtml(this.dataset.contactUrl)}">Contact us</a>
            <a class="button button--secondary" href="${escapeHtml(this.collectionUrl)}">Browse all products</a>
            <button class="button button--tertiary" type="button" data-dry-eye-finder-restart>Start again</button>
          </div>
        </section>`;
      this.focusHeading();
    }

    renderUnavailable() {
      this.screen.innerHTML = `
        <section class="dry-eye-finder__state">
          <h2 tabindex="-1">Recommendations are temporarily unavailable</h2>
          <p>The collection and manual filters are still available. Please browse all products or ask our team for help.</p>
          <div class="dry-eye-finder__state-actions">
            <a class="button" href="${escapeHtml(this.collectionUrl)}">Browse all products</a>
            <a class="button button--secondary" href="${escapeHtml(this.dataset.contactUrl)}">Contact us</a>
            <button class="button button--tertiary" type="button" data-dry-eye-finder-restart>Start again</button>
          </div>
        </section>`;
      this.focusHeading();
    }

    renderStandardResult(result, eligible) {
      const productCards = eligible
        .map((product, index) => this.resultCard(product, index))
        .filter(Boolean)
        .join('');
      const viewAllUrl = Core.buildFilterUrl(this.collectionUrl, result);
      const carouselId = `DryEyeFinderCarousel-${this.sectionId}`;
      const carouselStatusId = `DryEyeFinderCarouselStatus-${this.sectionId}`;
      const hasTiedPrimary = result.primaryCategories.length > 1;
      const resultHeading = hasTiedPrimary
        ? 'Your best starting points'
        : `Your best starting point: ${result.primary}`;
      const resultCopy = hasTiedPrimary
        ? `${result.primaryCategories.join(' and ')} are equally suitable starting points based on your answers.`
        : result.copy;
      this.screen.innerHTML = `
        <section class="dry-eye-finder__result">
          <p class="dry-eye-finder__eyebrow">Your personalised starting point</p>
          <h2 tabindex="-1">${escapeHtml(resultHeading)}</h2>
          <p class="dry-eye-finder__result-copy">${escapeHtml(resultCopy)}</p>
          ${
            result.itchingAdvisory
              ? '<p class="dry-eye-finder__itching-advisory">Persistent or dominant itching can have causes other than dry eye and may require professional advice.</p>'
              : ''
          }
          ${result.secondary ? `<p class="dry-eye-finder__secondary"><strong>Also consider:</strong> ${escapeHtml(result.secondary)}</p>` : ''}
          <h3>We found ${eligible.length} suitable ${eligible.length === 1 ? 'product' : 'products'}</h3>
          <div class="dry-eye-finder__carousel" aria-label="Matching products">
            <div class="dry-eye-finder__carousel-toolbar">
              <p class="dry-eye-finder__carousel-status" id="${escapeHtml(carouselStatusId)}" data-dry-eye-finder-carousel-status aria-live="polite"></p>
              <div class="dry-eye-finder__carousel-controls" aria-label="Carousel controls">
                <button
                  class="dry-eye-finder__carousel-button"
                  type="button"
                  data-dry-eye-finder-carousel-direction="-1"
                  aria-controls="${escapeHtml(carouselId)}"
                  aria-label="Previous matching products"
                ><span aria-hidden="true">&#8249;</span></button>
                <button
                  class="dry-eye-finder__carousel-button"
                  type="button"
                  data-dry-eye-finder-carousel-direction="1"
                  aria-controls="${escapeHtml(carouselId)}"
                  aria-label="Next matching products"
                ><span aria-hidden="true">&#8250;</span></button>
              </div>
            </div>
            <ul
              class="dry-eye-finder__results-grid product-grid grid no-dawn"
              id="${escapeHtml(carouselId)}"
              data-dry-eye-finder-carousel-track
              aria-label="Recommended products"
              aria-describedby="${escapeHtml(carouselStatusId)}"
            >${productCards}</ul>
          </div>
          <div class="dry-eye-finder__result-actions">
            <a class="button" href="${escapeHtml(viewAllUrl)}" data-dry-eye-finder-view-all>View all matching products</a>
            <button class="button button--secondary" type="button" data-dry-eye-finder-restart>Start again</button>
          </div>
          <p class="dry-eye-finder__result-note"><em>If your symptoms persist, frequently return or worsen, book an eye examination.</em></p>
        </section>`;
      this.initCarousel();
      this.focusHeading();
    }

    initCarousel() {
      this.teardownCarousel();
      const track = this.screen.querySelector('[data-dry-eye-finder-carousel-track]');
      if (!track) return;

      const update = () => {
        const cards = [...track.children];
        const status = this.screen.querySelector('[data-dry-eye-finder-carousel-status]');
        const previous = this.screen.querySelector('[data-dry-eye-finder-carousel-direction="-1"]');
        const next = this.screen.querySelector('[data-dry-eye-finder-carousel-direction="1"]');
        if (!cards.length || !status) return;

        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const cardWidth = cards[0].getBoundingClientRect().width;
        const step = Math.max(1, cardWidth + gap);
        const start = Math.min(cards.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
        const visible = Math.max(1, Math.floor((track.clientWidth + gap) / step));
        const end = Math.min(cards.length, start + visible);
        status.textContent = `Showing ${start + 1}–${end} of ${cards.length}`;
        if (previous) previous.disabled = start === 0;
        if (next) next.disabled = end >= cards.length;
      };

      const scheduleUpdate = () => {
        if (this.carouselFrame) cancelAnimationFrame(this.carouselFrame);
        this.carouselFrame = requestAnimationFrame(update);
      };
      track.addEventListener('scroll', scheduleUpdate, { passive: true });
      this.carouselScrollHandler = scheduleUpdate;
      if ('ResizeObserver' in window) {
        this.carouselObserver = new ResizeObserver(scheduleUpdate);
        this.carouselObserver.observe(track);
      }
      requestAnimationFrame(update);
    }

    teardownCarousel() {
      const track = this.screen?.querySelector('[data-dry-eye-finder-carousel-track]');
      if (track && this.carouselScrollHandler) track.removeEventListener('scroll', this.carouselScrollHandler);
      this.carouselObserver?.disconnect();
      this.carouselObserver = null;
      this.carouselScrollHandler = null;
      if (this.carouselFrame) cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = null;
    }

    resultCard(product, index) {
      if (!product.cardHtml) return '';
      const template = document.createElement('template');
      template.innerHTML = product.cardHtml.trim();
      const item = template.content.firstElementChild;
      if (!item) return '';

      item.classList.remove('scroll-trigger', 'animate--slide-in');
      item.removeAttribute('data-cascade');
      item.removeAttribute('style');
      item.dataset.dryEyeFinderResultProduct = product.handle;
      item.querySelectorAll('.quick-add, quick-add-modal, modal-opener').forEach((element) => element.remove());

      const prefix = `DryEyeFinderResult-${this.sectionId}-${index}-`;
      const idMap = new Map();
      item.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.id;
        const newId = `${prefix}${oldId}`;
        idMap.set(oldId, newId);
        element.id = newId;
      });
      ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach((attribute) => {
        item.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const values = element.getAttribute(attribute).split(/\s+/).map((value) => idMap.get(value) || value);
          element.setAttribute(attribute, values.join(' '));
        });
      });
      return item.outerHTML;
    }

    onScreenClick(event) {
      const carouselButton = event.target.closest('[data-dry-eye-finder-carousel-direction]');
      if (carouselButton) {
        const track = this.screen.querySelector('[data-dry-eye-finder-carousel-track]');
        const direction = Number(carouselButton.dataset.dryEyeFinderCarouselDirection) || 0;
        if (track && direction) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          track.scrollBy({ left: direction * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
        return;
      }

      const restart = event.target.closest('[data-dry-eye-finder-restart]');
      if (restart) {
        this.track('started_again');
        this.answers = {};
        this.step = 0;
        this.completed = false;
        this.renderQuestion();
        this.focusHeading();
        return;
      }

      const viewAll = event.target.closest('[data-dry-eye-finder-view-all]');
      if (viewAll) this.track('view_all_clicked', { category: Core.calculateResult(this.answers).primary });

      const productLink = event.target.closest('[data-dry-eye-finder-result-product] a[href*="/products/"]');
      if (productLink) {
        const handle = productLink.closest('[data-dry-eye-finder-result-product]')?.dataset.dryEyeFinderResultProduct;
        this.track('product_clicked', { product_handle: handle });
      }

      const analyticsLink = event.target.closest('[data-dry-eye-finder-analytics]');
      if (analyticsLink) this.track(analyticsLink.dataset.dryEyeFinderAnalytics);
    }

    focusHeading() {
      requestAnimationFrame(() => this.screen.querySelector('h2[tabindex="-1"], legend[tabindex="-1"]')?.focus());
    }

    async preloadProducts() {
      if (this.products.length > 0) return this.products;
      if (this.productsPromise) return this.productsPromise;
      this.productsPromise = this.loadAllProducts()
        .then((products) => {
          this.products = products;
          return products;
        })
        .catch((error) => {
          this.productsPromise = null;
          throw error;
        });
      return this.productsPromise;
    }

    async loadAllProducts() {
      if (!this.sectionId) throw new Error('Missing collection section ID');
      const firstPage = await this.fetchProductPage(1);
      const pages = Math.max(1, Number(firstPage.pages) || 1);
      const remaining = pages > 1 ? await Promise.all(Array.from({ length: pages - 1 }, (_, index) => this.fetchProductPage(index + 2))) : [];
      const orderedPages = [firstPage, ...remaining].sort((a, b) => a.page - b.page);
      return orderedPages.flatMap((page) => page.products);
    }

    async fetchProductPage(page) {
      const url = new URL(this.collectionUrl, window.location.origin);
      url.search = '';
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('page', String(page));
      url.searchParams.set('sort_by', 'manual');
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection page ${page} returned ${response.status}`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const dataScript = parsed.querySelector('[data-dry-eye-finder-products]');
      if (!dataScript) throw new Error(`Collection page ${page} did not include finder data`);
      const products = JSON.parse(dataScript.textContent || '[]');
      const cards = new Map(
        [...parsed.querySelectorAll('[data-dry-eye-finder-product]')].map((item) => [item.dataset.dryEyeFinderProduct, item.outerHTML])
      );
      products.forEach((product) => {
        product.cardHtml = cards.get(product.handle) || '';
      });
      return {
        page: Number(dataScript.dataset.page) || page,
        pages: Number(dataScript.dataset.pages) || 1,
        products,
      };
    }

    track(action, details = {}) {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function' || !privacy.analyticsProcessingAllowed()) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: `dry_eye_finder_${action}`,
        finder_version: '20260807-01',
        ...details,
      });
    }
  }

  if (!customElements.get('dry-eye-product-finder')) {
    customElements.define('dry-eye-product-finder', DryEyeProductFinder);
  }
})();
